/**
 * Serialize / deserialize transfer statistics view query params for shareable URLs.
 * Uses URLSearchParams only (no manual encodeURIComponent) to avoid double-encoding.
 */

export type TransferStatsUrlPayload = {
  firstLine: string;
  firstDestId: string;
  secondLine: string;
  secondOrigId: string;
  at: string;
};

const KEYS = {
  firstLine: 'firstLine',
  firstDestId: 'firstDestId',
  secondLine: 'secondLine',
  secondOrigId: 'secondOrigId',
  at: 'at',
} as const;

export function serializeTransferStatsToSearchParams(
  payload: TransferStatsUrlPayload
): URLSearchParams {
  const params = new URLSearchParams();
  params.set(KEYS.firstLine, payload.firstLine);
  params.set(KEYS.firstDestId, payload.firstDestId);
  params.set(KEYS.secondLine, payload.secondLine);
  params.set(KEYS.secondOrigId, payload.secondOrigId);
  if (payload.at) params.set(KEYS.at, payload.at);
  return params;
}

/**
 * Returns null if required API fields are missing (invalid or empty URL payload).
 */
export function deserializeTransferStatsFromSearchParams(
  searchParams: URLSearchParams
): TransferStatsUrlPayload | null {
  const firstLine = searchParams.get(KEYS.firstLine)?.trim() ?? '';
  const firstDestId = searchParams.get(KEYS.firstDestId)?.trim() ?? '';
  const secondLine = searchParams.get(KEYS.secondLine)?.trim() ?? '';
  const secondOrigId = searchParams.get(KEYS.secondOrigId)?.trim() ?? '';
  const at = searchParams.get(KEYS.at)?.trim() ?? '';

  if (!firstLine || !firstDestId || !secondLine || !secondOrigId) {
    return null;
  }

  return {
    firstLine,
    firstDestId,
    secondLine,
    secondOrigId,
    at,
  };
}

/** Minimal leg-shaped objects for getLineName / header copy (URL-driven load). */
export function buildLegStubsFromTransferUrlPayload(payload: TransferStatsUrlPayload) {
  const firstLeg = {
    line: { name: payload.firstLine },
    destination: {
      id: payload.firstDestId,
      name: payload.at || payload.firstDestId,
    },
    origin: { id: '', name: '' },
  };
  const secondLeg = {
    line: { name: payload.secondLine },
    origin: { id: payload.secondOrigId, name: '' },
    destination: { id: '', name: '' },
  };
  return { firstLeg, secondLeg };
}

export function transferStatsPayloadFromLegs(
  firstLeg: { line: { name: string }; destination: { id: string; name: string } },
  secondLeg: { line: { name: string }; origin: { id: string } }
): TransferStatsUrlPayload {
  return {
    firstLine: firstLeg.line.name,
    firstDestId: String(firstLeg.destination.id),
    secondLine: secondLeg.line.name,
    secondOrigId: String(secondLeg.origin.id),
    at: firstLeg.destination.name,
  };
}
